import { useEffect, useState } from 'react';
import { Platform } from 'react-native';

import { useUiResources } from '@fhs/ui';
import { prefetchHomepageOrderState } from 'hooks/use-get-homepage-order-state';
import NativeAuthStorage from 'utils/cognito/storage';
import { isWeb } from 'utils/environment';
import { waitForClientInitialization } from 'utils/launchdarkly';
import { WebLocalStorage } from 'utils/local-storage/web-local-storage';
import logger from 'utils/logger';

/**
 * -- Global App Initialization status --
 *
 * Loading - Blocks the app from rendering
 * Initialized - All data is there and the app can load without crashing
 * Error - Prevents the app from rendering at all - TODO: RN - Add better feedback to guests to resolve the errors (ex. Network errors)
 *
 * */
export enum initStatuses {
  LOADING = 'loading',
  INITIALIZED = 'initialized',
  ERROR = 'error',
}

export const useAppBlockerInit = () => {
  const [state, setState] = useState(isWeb ? initStatuses.INITIALIZED : initStatuses.LOADING);
  const [uiResourcesLoaded, uiResourcesError] = useUiResources();

  useEffect(() => {
    const prepare = async () => {
      try {
        const authSync = NativeAuthStorage.sync(); // this is called internally by Cognito.configure() but it doesn't give us a promise!

        // Get the homepage order state the instant auth is available (native only), don't block init
        if (Platform.OS !== 'web') {
          void authSync.then(() => prefetchHomepageOrderState());
        }

        await Promise.all([
          authSync,
          WebLocalStorage.sync(),
          waitForClientInitialization().catch(() => {
            // noop
            // Even if this errors, we don't want to prevent initialization after timeout because LD will try still
            // connect in the background, the user will just be using the app with default flag values.
          }),
        ]);

        setState(initStatuses.INITIALIZED);
      } catch (error) {
        logger.error({ error, message: 'useAppBlockerInit encountered an error' });
        setState(initStatuses.ERROR);
      }
    };
    prepare();
  }, []);

  useEffect(() => {
    if (uiResourcesError) {
      logger.error(uiResourcesError);
    }
  }, [uiResourcesError]);

  if (uiResourcesError && Platform.OS === 'web') {
    // On web, we need to ignore the `uiResourcesLoaded` when an error occurs.
    // this means the font failed to load, but we should still be able to continue forward.
    if (Platform.OS === 'web') {
      return state;
    }
    return initStatuses.ERROR;
  }
  return uiResourcesLoaded ? state : initStatuses.LOADING;
};
